import React, { useState, useEffect, useRef } from 'react';
import { cardsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Printer, Download, QrCode, Store } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StoreQRPage = () => {
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('');
  const [loading, setLoading] = useState(true);
  const [storeData, setStoreData] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const { data } = await cardsAPI.list();
      setCards(data);
      if (data.length > 0) {
        setSelectedCard(data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch cards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCard) {
      fetchStoreQR();
    }
  }, [selectedCard]);

  const fetchStoreQR = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/merchant/store-qr`, { withCredentials: true });
      setStoreData(data);
    } catch (err) {
      console.error('Failed to fetch store QR:', err);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const doc = win.document;
    doc.open();
    const storeName = (selectedCardData?.name || 'LoyalPass').replace(/[<>&"']/g, '');
    doc.write([
      '<!DOCTYPE html><html><head>',
      `<title>QR Code - ${storeName}</title>`,
      '<style>',
      '* { margin: 0; padding: 0; box-sizing: border-box; }',
      "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: white; }",
      '.container { text-align: center; padding: 40px; }',
      '.store-name { font-size: 28px; font-weight: 800; margin-bottom: 8px; }',
      '.card-name { font-size: 18px; color: #666; margin-bottom: 24px; }',
      '.qr-box { display: inline-block; padding: 24px; border: 3px solid #000; border-radius: 16px; margin-bottom: 24px; }',
      '.scan-text { font-size: 20px; font-weight: 700; margin-bottom: 8px; }',
      '.sub-text { font-size: 14px; color: #888; }',
      '.card-type-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; margin-bottom: 16px; }',
      '@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }',
      '</style></head><body>',
      '</body></html>'
    ].join('\n'));
    doc.close();
    // Safely clone content into the new window body
    const clone = printContent.cloneNode(true);
    win.document.body.appendChild(clone);
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  // High-resolution PNG (1200 px, white margin): opens and saves on any phone,
  // and is sharp enough for an A5/A4 print.
  const handleDownloadImage = () => {
    const svg = document.querySelector('#store-qr-svg svg');
    if (!svg) return;
    const svgString = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const svgUrl = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml' }));
    img.onload = () => {
      const size = 1200;
      const margin = 120;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, margin, margin, size - margin * 2, size - margin * 2);
      URL.revokeObjectURL(svgUrl);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${(selectedCardData?.name || 'boutique').replace(/\s+/g, '-')}.png`;
        // The link must be in the page for the file name to be kept.
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    };
    img.src = svgUrl;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const selectedCardData = cards.find(c => c.id === selectedCard);
  const currentQR = storeData?.qr_codes?.find(q => q.card_id === selectedCard);

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto">
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
          QR Code Boutique
        </h1>
        <p className="text-slate-600 mt-1">Imprimez et affichez en boutique pour que vos clients rejoignent votre programme</p>
      </div>

      {/* Card Selection */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <label className="text-sm font-medium text-slate-700 mb-2 block">Carte de fidélité</label>
          <Select value={selectedCard} onValueChange={setSelectedCard}>
            <SelectTrigger data-testid="store-qr-card-select">
              <SelectValue placeholder="Choisir une carte" />
            </SelectTrigger>
            <SelectContent>
              {cards.map((card) => (
                <SelectItem key={card.id} value={card.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: card.primary_color }} />
                    {card.name}
                    <span className="text-xs text-slate-400 ml-1">
                      ({card.card_type === 'stamps' ? 'Tampons' : 'Points'})
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* QR Code Display */}
      {selectedCardData && (
        <Card className="border-slate-200">
          <CardContent className="p-5 sm:p-8">
            <div ref={printRef}>
              <div className="container text-center">
                <p className="store-name text-2xl font-extrabold text-slate-900 mb-1">
                  {storeData?.merchant_name || 'Ma Boutique'}
                </p>
                {(selectedCardData.name || '').trim().toLowerCase() !== (storeData?.merchant_name || '').trim().toLowerCase() && (
                  <p className="card-name text-lg text-slate-500 mb-2">{selectedCardData.name}</p>
                )}
                <div className="mb-4">
                  <span
                    className="card-type-badge inline-block px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: selectedCardData.primary_color + '20',
                      color: selectedCardData.primary_color
                    }}
                  >
                    {selectedCardData.card_type === 'stamps'
                      ? `Carte ${selectedCardData.total_stamps || 10} tampons`
                      : `${selectedCardData.points_per_euro ?? selectedCardData.points_per_visit ?? 1} point(s) par euro`
                    }
                  </span>
                </div>
                <div className="qr-box inline-block p-6 border-4 border-slate-900 rounded-2xl mb-6" id="store-qr-svg">
                  <QRCodeSVG
                    value={currentQR?.join_url || `${window.location.origin}/join/${selectedCard}`}
                    size={220}
                    level="H"
                    includeMargin={false}
                    fgColor="#000000"
                  />
                </div>
                <p className="scan-text text-xl font-bold text-slate-900 mb-1">
                  Scannez pour rejoindre notre programme de fidélité
                </p>
                <p className="sub-text text-sm text-slate-500">
                  {selectedCardData.card_type === 'stamps'
                    ? `Collectez ${selectedCardData.total_stamps || 10} tampons pour obtenir : ${selectedCardData.reward_description}`
                    : `${selectedCardData.points_per_euro ?? selectedCardData.points_per_visit ?? 1} point(s) par euro dépensé. ${selectedCardData.reward_threshold} points = ${selectedCardData.reward_description}`
                  }
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <Button
                onClick={handlePrint}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6"
                data-testid="print-qr-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer le QR
              </Button>
              <Button
                onClick={handleDownloadImage}
                variant="outline"
                className="px-6"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger l’image
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Store className="w-5 h-5" /> Comment utiliser
          </h3>
          <ol className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2">
              <span className="font-bold text-blue-600">1.</span>
              Sélectionnez la carte de fidélité de votre boutique
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-blue-600">2.</span>
              Appuyez sur « Imprimer le QR », ou « Télécharger l’image » pour l’envoyer à un imprimeur
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-blue-600">3.</span>
              Affichez-le au comptoir ou à l'entrée
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-blue-600">4.</span>
              Les clients scannent avec leur téléphone pour rejoindre votre programme
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreQRPage;
